import React, { Component, createContext } from 'react';
import api from '../services/api';

export const NotificationsContext = createContext();

export class NotificationsProvider extends Component {
  constructor(props) {
    super(props);
    this.state = {
      notifications: [],
      unreadCount: 0,
      loading: true,
    };

    this.pollInterval = null;
  }

  componentDidMount() {
    // Delay initial fetch to avoid race conditions on page load
    setTimeout(() => {
      const token = localStorage.getItem('token');
      if (token) {
        this.fetchNotifications();
        this.startPolling();
      } else {
        this.setState({ loading: false });
      }
    }, 500);
  }

  componentWillUnmount() {
    this.stopPolling();
  }

  startPolling = () => {
    // Poll for new notifications every 30 seconds
    this.pollInterval = setInterval(() => {
      const token = localStorage.getItem('token');
      if (token) {
        this.fetchUnreadCount();
      } else {
        this.stopPolling();
      }
    }, 30000);
  };

  stopPolling = () => {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  };

  fetchNotifications = async () => {
    try {
      const response = await api.get('/notifications');
      this.setState({
        notifications: response.data.notifications,
        unreadCount: response.data.unreadCount,
        loading: false,
      });
    } catch (error) {
      // Don't log 401 errors (just means user isn't authenticated)
      if (error.response?.status !== 401) {
        console.error('Failed to fetch notifications:', error);
      }
      this.setState({ loading: false });
    }
  };

  fetchUnreadCount = async () => {
    try {
      const response = await api.get('/notifications/unread-count');
      this.setState({ unreadCount: response.data.count });
    } catch (error) {
      // Silently fail on 401 (user not authenticated)
      if (error.response?.status !== 401) {
        console.error('Failed to fetch unread count:', error);
      }
    }
  };

  markAsRead = async (notificationId) => {
    try {
      await api.patch(`/notifications/${notificationId}/read`);
      await this.fetchNotifications();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  markAsUnread = async (notificationId) => {
    try {
      await api.patch(`/notifications/${notificationId}/unread`);
      await this.fetchNotifications();
    } catch (error) {
      console.error('Failed to mark as unread:', error);
    }
  };

  markAllAsRead = async () => {
    try {
      await api.patch('/notifications/mark-all-read');
      await this.fetchNotifications();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  deleteNotification = async (notificationId) => {
    try {
      await api.delete(`/notifications/${notificationId}`);
      await this.fetchNotifications();
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  addNotification = (notification) => {
    this.setState((prevState) => ({
      notifications: [notification, ...prevState.notifications],
      unreadCount: prevState.unreadCount + 1,
    }));
  };

  // Reset notifications on logout
  reset = () => {
    this.stopPolling();
    this.setState({
      notifications: [],
      unreadCount: 0,
      loading: true,
    });
  };

  render() {
    return (
      <NotificationsContext.Provider
        value={{
          ...this.state,
          markAsRead: this.markAsRead,
          markAsUnread: this.markAsUnread,
          markAllAsRead: this.markAllAsRead,
          deleteNotification: this.deleteNotification,
          addNotification: this.addNotification,
          refreshNotifications: this.fetchNotifications,
          reset: this.reset,
          startPolling: this.startPolling,
        }}
      >
        {this.props.children}
      </NotificationsContext.Provider>
    );
  }
}

export const useNotifications = () => {
  const context = React.useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};